import React, { useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Shield, Lock, Eye, Database, Mail, Phone } from 'lucide-react';
import SEOHead from '../components/SEOHead';

const PrivacyPolicy = () => {
  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const sections = [
    {
      icon: Eye,
      title: "Information Collection & Data Types",
      content: `We collect various types of information to provide and improve our AI chatbot services:

PERSONAL INFORMATION:
• Identity Data: Full name, business name, job title, contact preferences
• Contact Data: Email address, phone number, postal address, country of residence
• Financial Data: Payment information, billing address, transaction history
• Technical Data: IP address, browser type, device information, operating system
• Usage Data: Service interaction patterns, feature usage, performance metrics

BUSINESS & TRAINING DATA:
• Company information, branding assets, logos, and marketing materials
• Website content, FAQs, product information, and customer service scripts
• Training datasets, conversation logs, and customer interaction data
• Integration data from connected systems, CRM, or databases
• Analytics data, chatbot performance metrics, and user engagement statistics

SENSITIVE DATA CATEGORIES:
• Data revealing personal opinions, beliefs, or preferences
• Location data when using mobile applications
• Communication metadata and conversation patterns
• Biometric data if voice features are enabled
• Any special category data you choose to include in training materials

AUTOMATIC DATA COLLECTION:
• Cookies, web beacons, and similar tracking technologies
• Server logs, error reports, and diagnostic information
• Security monitoring data and access logs
• Performance monitoring and optimization data
• Third-party integration data and API usage statistics

DATA SOURCES:
• Information provided directly by you during registration and use
• Data collected automatically through our platform and services
• Information from connected third-party services and integrations
• Publicly available information related to your business
• Data from affiliates, partners, and service providers`
    },
    {
      icon: Database,
      title: "Data Processing Purposes & Legal Basis",
      content: `We process your data for multiple purposes under various legal bases:

PRIMARY SERVICE DELIVERY (Contract & Legitimate Interest):
• Creating, training, and deploying custom AI chatbots
• Processing payments and managing subscription services
• Providing technical support and customer service
• Maintaining and optimizing platform performance
• Ensuring service security and preventing fraud

COMMUNICATION & MARKETING (Consent & Legitimate Interest):
• Sending service notifications and account updates
• Providing customer support and technical assistance
• Marketing communications about new features and services
• Newsletter distribution and product announcements
• Customer satisfaction surveys and feedback collection

LEGAL & COMPLIANCE (Legal Obligation):
• Complying with applicable laws and regulations
• Responding to legal requests and court orders
• Maintaining records for audit and tax purposes
• Preventing money laundering and fraud
• Data protection impact assessments

BUSINESS OPERATIONS (Legitimate Interest):
• Analytics and performance monitoring
• Research and development of new features
• Quality assurance and service improvement
• Risk management and business continuity
• Corporate transactions and business transfers

AI TRAINING & IMPROVEMENT (Legitimate Interest & Consent):
• Training machine learning models and algorithms
• Improving AI response accuracy and relevance
• Developing new AI capabilities and features
• Benchmarking and performance optimization
• Creating anonymized datasets for research

LEGAL BASIS JUSTIFICATION:
• Contract: Processing necessary for service delivery
• Legitimate Interest: Business operations and service improvement
• Consent: Marketing communications and optional features
• Legal Obligation: Compliance with applicable laws
• Vital Interest: Protection of health and safety (rarely applicable)`
    },
    {
      icon: Lock,
      title: "Advanced Security & Protection Measures",
      content: `We implement comprehensive security measures to protect your data:

ENCRYPTION & DATA PROTECTION:
• AES-256 encryption for data at rest
• TLS 1.3 encryption for data in transit
• End-to-end encryption for sensitive communications
• Database encryption with hardware security modules
• Encrypted backups with geographically distributed storage

ACCESS CONTROLS & AUTHENTICATION:
• Multi-factor authentication for employee access
• Role-based access control with principle of least privilege
• Regular access reviews and permission audits
• Secure authentication protocols and session management
• Automated account lockout for suspicious activities

INFRASTRUCTURE SECURITY:
• ISO 27001 certified data centers
• 24/7 security monitoring and incident response
• Regular penetration testing and vulnerability assessments
• Network segmentation and firewall protection
• Intrusion detection and prevention systems

DATA BACKUP & RECOVERY:
• Automated daily backups with 99.9% reliability
• Geographically distributed backup locations
• Point-in-time recovery capabilities
• Disaster recovery procedures with RTO < 4 hours
• Regular backup restoration testing

EMPLOYEE SECURITY:
• Comprehensive background checks for all staff
• Mandatory security training and certification
• Confidentiality agreements and non-disclosure obligations
• Regular security awareness training and phishing simulations
• Incident reporting procedures and protocols

COMPLIANCE CERTIFICATIONS:
• GDPR compliance with documented procedures
• SOC 2 Type II certification (in progress)
• ISO 27001 information security management
• Regular third-party security audits
• Privacy by design and by default principles

INCIDENT RESPONSE:
• 24/7 security operations center monitoring
• Automated threat detection and response
• Incident response team with defined escalation procedures
• Customer notification within 72 hours of data breaches
• Forensic investigation capabilities and evidence preservation`
    },
    {
      icon: Shield,
      title: "Data Sharing, Transfers & Third Parties",
      content: `We share your data only under specific circumstances and with appropriate safeguards:

SERVICE PROVIDERS & PROCESSORS:
• Cloud hosting providers (AWS, Google Cloud, Microsoft Azure)
• Payment processors (Stripe, PayPal) for transaction handling
• Email service providers for communications and notifications
• Analytics providers for service improvement and optimization
• Customer support platforms and helpdesk solutions

LEGAL & REGULATORY SHARING:
• Law enforcement agencies when legally required
• Regulatory authorities for compliance purposes
• Courts and legal proceedings as mandated by law
• Government agencies for national security matters
• Tax authorities for financial reporting obligations

BUSINESS TRANSFERS:
• Mergers, acquisitions, or asset sales
• Corporate restructuring or reorganization
• Bankruptcy or insolvency proceedings
• Due diligence for potential transactions
• Successor entities with equivalent privacy protections

INTERNATIONAL DATA TRANSFERS:
• Transfers to EU/EEA countries with adequate protection
• Standard Contractual Clauses for non-adequate countries
• Adequacy decisions by European Commission
• Binding Corporate Rules for intra-corporate transfers
• Consent for specific transfers when appropriate

THIRD-PARTY SERVICES WE DO NOT SHARE WITH:
• Data brokers or advertising networks
• Social media platforms (unless you explicitly connect)
• Marketing agencies (without your consent)
• Competitors or unauthorized third parties
• Any entity without legitimate business purpose

SAFEGUARDS FOR INTERNATIONAL TRANSFERS:
• Data Processing Agreements with all processors
• Regular audits of third-party security practices
• Contractual obligations for data protection
• Right to terminate agreements for privacy violations
• Transparency reports on government data requests

YOUR CONTROL OVER SHARING:
• Opt-out options for marketing communications
• Granular consent for optional data sharing
• Ability to withdraw consent at any time
• Data portability rights for business continuity
• Right to object to processing for legitimate interests`
    },
    {
      icon: Shield,
      title: "Data Retention & Deletion Policies",
      content: `We maintain specific retention periods for different types of data:

ACCOUNT & PROFILE DATA:
• Active accounts: Retained while account is active
• Inactive accounts: Deleted after 3 years of inactivity
• Deleted accounts: Data removed within 30 days
• Backup retention: 90 days after account deletion
• Legal hold: Extended retention for legal proceedings

TRAINING & CHATBOT DATA:
• Active chatbots: Retained while service is active
• Deactivated chatbots: Data retained for 12 months
• Training datasets: Anonymized and retained for AI improvement
• Conversation logs: Deleted after 24 months unless required for service
• Performance metrics: Aggregated data retained indefinitely

FINANCIAL & BILLING DATA:
• Payment records: Retained for 7 years for tax compliance
• Invoice data: Retained for 10 years per accounting standards
• Failed payment attempts: Deleted after 12 months
• Refund records: Retained for 7 years minimum
• Tax documentation: Retained per applicable tax laws

COMMUNICATION & SUPPORT DATA:
• Customer support tickets: Retained for 5 years
• Email communications: Deleted after 3 years
• Chat logs: Deleted after 12 months
• Call recordings: Deleted after 24 months
• Feedback and surveys: Anonymized after 2 years

TECHNICAL & SECURITY DATA:
• Server logs: Retained for 12 months
• Security incident logs: Retained for 7 years
• Access logs: Deleted after 24 months
• Error reports: Deleted after 18 months
• Monitoring data: Aggregated and anonymized after 6 months

AUTOMATED DELETION PROCEDURES:
• Scheduled deletion processes run monthly
• Confirmation emails sent before deletion
• Data export options available before deletion
• Secure deletion using DoD 5220.22-M standards
• Certificate of destruction for sensitive data`
    }
  ];

  return (
    <div className="pt-16 min-h-screen bg-gray-950 relative overflow-hidden">
      <SEOHead 
        title="Privacy Policy - BrixBot AI Data Protection" 
        description="BrixBot's comprehensive privacy policy covering AI data processing, GDPR compliance, and your privacy rights. Learn how we protect and handle your data."
        isIndexable={true}
      />
      {/* Background Elements */}
      <div className="fixed inset-0 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-950 via-gray-900 to-violet-950/30"></div>
        <div className="absolute inset-0 bg-grid-pattern opacity-20"></div>
        
        {/* Floating Elements */}
        <div className="absolute top-20 left-10 w-32 h-32 bg-violet-500/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-48 h-48 bg-purple-500/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>

      <div className="relative z-10 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-violet-500 to-purple-600 rounded-3xl mb-8">
              <Shield className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Privacy Policy
            </h1>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Comprehensive privacy protection covering AI data processing, international compliance (GDPR, CCPA), and your rights. We are committed to transparency in how we collect, use, and protect your personal information.
            </p>
            <div className="mt-6 text-sm text-gray-500">
              Last updated: August 12, 2025 | Effective: August 12, 2025 | Version 3.0
            </div>
          </div>

          {/* Main Content */}
          <div className="space-y-8">
            {sections.map((section, index) => (
              <Card key={index} className="bg-gray-900/50 border-violet-500/20 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="text-2xl text-white flex items-center">
                    <div className="w-12 h-12 bg-gradient-to-br from-violet-500 to-purple-600 rounded-2xl flex items-center justify-center mr-4">
                      <section.icon className="w-6 h-6 text-white" />
                    </div>
                    {section.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-gray-300 whitespace-pre-line leading-relaxed">
                    {section.content}
                  </div>
                </CardContent>
              </Card>
            ))}

            {/* Privacy Contact & Data Protection Officer */}
            <Card className="bg-gradient-to-r from-violet-900/30 to-purple-900/30 border-violet-500/20 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-2xl text-white flex items-center">
                  <Mail className="w-6 h-6 mr-3 text-violet-400" />
                  Privacy Contact & Data Protection
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6 text-gray-300">
                  <div>
                    <h4 className="font-semibold text-white mb-3">Data Protection Officer (DPO)</h4>
                    <p className="mb-2">Our appointed Data Protection Officer oversees privacy compliance:</p>
                    <div className="bg-gray-800/50 p-4 rounded-lg">
                      <p><strong>DPO Email:</strong> <span className="text-violet-400">support@brixbot.ai</span></p>
                      <p><strong>Privacy Inquiries:</strong> <span className="text-violet-400">support@brixbot.ai</span></p>
                      <p><strong>Data Breach Reports:</strong> <span className="text-violet-400">support@brixbot.ai</span></p>
                      <p><strong>General Support:</strong> <span className="text-violet-400">support@brixbot.ai</span></p>
                      <p><strong>Phone:</strong> <span className="text-violet-400">+421915721942</span></p>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold text-white mb-3">Response Times & Escalation</h4>
                    <ul className="list-disc list-inside ml-4 space-y-1">
                      <li><strong>Privacy Rights Requests:</strong> 30 days maximum (GDPR) / 45 days (CCPA)</li>
                      <li><strong>Data Breach Notifications:</strong> Within 72 hours to authorities, individuals</li>
                      <li><strong>General Privacy Questions:</strong> 2-3 business days</li>
                      <li><strong>Complex Legal Matters:</strong> 5-10 business days</li>
                      <li><strong>Urgent Security Issues:</strong> Within 4 hours</li>
                    </ul>
                  </div>

                  <div>
                    <h4 className="font-semibold text-white mb-3">Supervisory Authorities</h4>
                    <p className="mb-2">You have the right to lodge complaints with relevant data protection authorities:</p>
                    <ul className="list-disc list-inside ml-4 space-y-1">
                      <li><strong>Slovakia:</strong> Úrad na ochranu osobných údajov (ÚOOÚ)</li>
                      <li><strong>EU/EEA:</strong> Your local data protection authority</li>
                      <li><strong>California:</strong> California Attorney General's Office</li>
                      <li><strong>Other Jurisdictions:</strong> Contact us for guidance on local authorities</li>
                    </ul>
                  </div>

                  <div>
                    <h4 className="font-semibold text-white mb-3">International Privacy Frameworks</h4>
                    <p className="mb-2">We comply with major international privacy frameworks:</p>
                    <ul className="list-disc list-inside ml-4 space-y-1">
                      <li>EU General Data Protection Regulation (GDPR)</li>
                      <li>California Consumer Privacy Act (CCPA) & CPRA</li>
                      <li>UK Data Protection Act 2018</li>
                      <li>Swiss Federal Data Protection Act</li>
                      <li>Brazil's Lei Geral de Proteção de Dados (LGPD)</li>
                      <li>Canada's Personal Information Protection and Electronic Documents Act (PIPEDA)</li>
                    </ul>
                  </div>

                  <div className="border-t border-gray-600 pt-4">
                    <h4 className="font-semibold text-white mb-2">Emergency Privacy Contact</h4>
                    <p className="text-sm">
                      For urgent privacy matters requiring immediate attention (data breaches, security incidents, legal emergencies):
                    </p>
                    <p className="text-violet-400 font-semibold mt-1">
                      Email: support@brixbot.ai | Phone: +421915721942 (24/7)
                    </p>
                  </div>

                  <div className="border-t border-gray-600 pt-4 mt-6">
                    <p className="text-sm text-gray-400">
                      <strong>Policy Version:</strong> 3.0 | <strong>Last Updated:</strong> August 12, 2025<br/>
                      <strong>Effective Date:</strong> August 12, 2025 | <strong>Next Review:</strong> December 2025
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Your Comprehensive Rights */}
            <Card className="bg-gray-900/50 border-violet-500/20 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-2xl text-white">Your Privacy Rights & Controls</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-gray-300 space-y-6">
                  <div>
                    <h4 className="font-semibold text-white mb-3">GDPR Rights (EU Residents)</h4>
                    <ul className="list-disc list-inside ml-4 space-y-2">
                      <li><strong>Right of Access:</strong> Request copies of your personal data and processing information</li>
                      <li><strong>Right to Rectification:</strong> Correct inaccurate or incomplete personal data</li>
                      <li><strong>Right to Erasure ("Right to be Forgotten"):</strong> Request deletion of your personal data</li>
                      <li><strong>Right to Restrict Processing:</strong> Limit how we process your data in certain circumstances</li>
                      <li><strong>Right to Data Portability:</strong> Receive your data in machine-readable format</li>
                      <li><strong>Right to Object:</strong> Object to processing based on legitimate interests</li>
                      <li><strong>Right to Withdraw Consent:</strong> Withdraw consent for consent-based processing</li>
                      <li><strong>Right to Lodge Complaints:</strong> File complaints with supervisory authorities</li>
                    </ul>
                  </div>

                  <div>
                    <h4 className="font-semibold text-white mb-3">CCPA Rights (California Residents)</h4>
                    <ul className="list-disc list-inside ml-4 space-y-2">
                      <li><strong>Right to Know:</strong> Information about data collection, use, and sharing</li>
                      <li><strong>Right to Delete:</strong> Request deletion of personal information</li>
                      <li><strong>Right to Opt-Out:</strong> Opt-out of sale of personal information</li>
                      <li><strong>Right to Non-Discrimination:</strong> Equal service regardless of privacy choices</li>
                      <li><strong>Right to Correct:</strong> Correct inaccurate personal information</li>
                      <li><strong>Right to Limit:</strong> Limit use of sensitive personal information</li>
                    </ul>
                  </div>

                  <div>
                    <h4 className="font-semibold text-white mb-3">How to Exercise Your Rights</h4>
                    <p className="mb-2">To exercise any privacy rights:</p>
                    <ul className="list-disc list-inside ml-4 space-y-1">
                      <li>Email: <span className="text-violet-400">support@brixbot.ai</span></li>
                      <li>Phone: <span className="text-violet-400">+421915721942</span></li>
                      <li>Written request to our registered address</li>
                      <li>Through your account settings (for some rights)</li>
                      <li>Via our online privacy request form (coming soon)</li>
                    </ul>
                    <p className="mt-2 text-sm text-gray-400">
                      We will respond to verified requests within 30 days (GDPR) or 45 days (CCPA).
                    </p>
                  </div>

                  <div>
                    <h4 className="font-semibold text-white mb-3">Verification & Processing</h4>
                    <p className="mb-2">For security, we verify your identity before processing requests through:</p>
                    <ul className="list-disc list-inside ml-4 space-y-1">
                      <li>Account login verification</li>
                      <li>Email address confirmation</li>
                      <li>Identity document verification (for sensitive requests)</li>
                      <li>Multi-factor authentication</li>
                      <li>Phone number verification</li>
                    </ul>
                  </div>

                  <div>
                    <h4 className="font-semibold text-white mb-3">Limitations & Exceptions</h4>
                    <p className="mb-2">Some rights may be limited by:</p>
                    <ul className="list-disc list-inside ml-4 space-y-1">
                      <li>Legal obligations requiring data retention</li>
                      <li>Ongoing legal proceedings or investigations</li>
                      <li>Technical impossibility or disproportionate effort</li>
                      <li>Rights of other individuals</li>
                      <li>Public interest or freedom of expression</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* AI & Machine Learning Privacy */}
            <Card className="bg-gradient-to-r from-violet-900/30 to-purple-900/30 border-violet-500/20 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-2xl text-white">AI & Machine Learning Privacy</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-gray-300 space-y-4">
                  <div>
                    <h4 className="font-semibold text-white mb-3">AI Training & Your Data</h4>
                    <p className="mb-2">Our AI systems learn from data in the following ways:</p>
                    <ul className="list-disc list-inside ml-4 space-y-1">
                      <li>Your training data is used to create your specific chatbot</li>
                      <li>Conversation patterns help improve response quality</li>
                      <li>Anonymized, aggregated data improves general AI capabilities</li>
                      <li>Error patterns help us fix bugs and improve accuracy</li>
                      <li>Usage statistics guide feature development</li>
                    </ul>
                  </div>

                  <div>
                    <h4 className="font-semibold text-white mb-3">Data Anonymization</h4>
                    <p className="mb-2">We anonymize data used for AI improvement through:</p>
                    <ul className="list-disc list-inside ml-4 space-y-1">
                      <li>Removal of personally identifiable information</li>
                      <li>Statistical aggregation of usage patterns</li>
                      <li>Tokenization and pseudonymization techniques</li>
                      <li>K-anonymity and differential privacy methods</li>
                      <li>Regular re-anonymization of datasets</li>
                    </ul>
                  </div>

                  <div>
                    <h4 className="font-semibold text-white mb-3">AI Decision Making</h4>
                    <p className="mb-2">Our AI systems make decisions about:</p>
                    <ul className="list-disc list-inside ml-4 space-y-1">
                      <li>Chatbot response generation and selection</li>
                      <li>Content filtering and safety measures</li>
                      <li>Service optimization and performance tuning</li>
                      <li>Fraud detection and security monitoring</li>
                      <li>Quality assessment and improvement recommendations</li>
                    </ul>
                    <p className="mt-2 text-sm text-yellow-300">
                      You have the right to request human review of AI decisions that significantly affect you.
                    </p>
                  </div>

                  <div>
                    <h4 className="font-semibold text-white mb-3">Opt-Out of AI Training</h4>
                    <p className="mb-2">You can opt-out of having your data used for AI improvement by:</p>
                    <ul className="list-disc list-inside ml-4 space-y-1">
                      <li>Contacting our privacy team at support@brixbot.ai</li>
                      <li>Adjusting settings in your account dashboard</li>
                      <li>Specifying opt-out preferences during onboarding</li>
                    </ul>
                    <p className="mt-2 text-sm text-gray-400">
                      Note: Opting out may affect the quality and performance of your specific chatbot.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;